import { Api } from './api.js';
import { beginReviewSession, readConfig } from './config.js';
import { currentMode } from './mode.js';
import { Overlay } from './ui.js';

// Captured synchronously: document.currentScript is only meaningful while the
// script is executing, and everything below may await.
const self = document.currentScript as HTMLScriptElement | null;

const boot = readConfig(self);

if (!boot.ok) {
  // A page nobody is reviewing is the normal case and says nothing. A
  // misconfiguration is worth one console line, because the alternative is
  // someone concluding the tool is broken when the tag is simply wrong.
  if (boot.loud) console.warn(`[annotate] ${boot.reason}`);
} else {
  void start();
}

async function start(): Promise<void> {
  if (!boot.ok) return;

  const mode = await currentMode(boot.config);

  if (mode === 'off') {
    // Someone typed `?annotate=1` and got nothing. Without this they would file a
    // bug against the widget for a setting in the project's own console.
    if (boot.requestedByUrl) {
      console.warn(
        `[annotate] review mode is off for project "${boot.config.projectId}". ` +
          `Turn it on in the project settings, or check the annotate server is reachable.`,
      );
    }
    return;
  }

  // `link` is the mode where the visitor has to have asked. `open` is the project
  // answering on their behalf, so the URL is not consulted at all.
  if (mode === 'link') {
    if (!boot.requestedByUrl) return;
    beginReviewSession(boot.config.projectId);
  }

  // Constructed here rather than at module scope: the overlay mounts itself into
  // document.body, which does not exist yet if the tag sits in <head>.
  const go = (): void => void new Overlay(new Api(boot.config), boot.config).start();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', go, { once: true });
  } else {
    go();
  }
}
